import { useState } from "react";
import { useTranslation } from "react-i18next";
import { googleAuthorizeUrl } from "@/lib/api";
import { Button, Card, ErrorBanner, Field, PageContainer, TextInput } from "@/components/Forms";
import { CalendarIcon, ExternalLinkIcon } from "@/components/Icons";

/**
 * Platform integrations page — currently only a jump-button to the
 * Phase 9B Google Calendar OAuth authorize endpoint. Admins pre-fill a
 * tenant slug + a sales_user_id and the popup walks the OAuth consent
 * flow; on success the callback stores the refresh token in
 * tenant_secret keyed by salesUserId.
 *
 * <p>Listing existing connections requires a tenant_secret read endpoint
 * that doesn't exist yet (deferred — would need careful audit semantics).
 * This page is intentionally lean.
 */
export default function IntegrationsPage() {
  const { t } = useTranslation();
  const [slug, setSlug] = useState("");
  const [salesUserId, setSalesUserId] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function open() {
    setErr(null);
    if (!slug.trim() || !salesUserId.trim()) {
      setErr(t("integrations.errorTenantAndSalesUserRequired"));
      return;
    }
    const url = googleAuthorizeUrl(slug.trim(), salesUserId.trim());
    window.open(url, "_blank", "noopener,noreferrer,width=520,height=720");
  }

  return (
    <PageContainer title={t("integrations.title")} subtitle={t("integrations.subtitle")}>
      <ErrorBanner message={err} />
      <Card
        title={t("integrations.googleTitle")}
        description={t("integrations.googleDescription")}
      >
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label={t("integrations.fieldTenant")} required>
            <TextInput
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={t("integrations.fieldTenantPlaceholder")}
            />
          </Field>
          <Field label={t("integrations.fieldSalesUser")} required>
            <TextInput
              value={salesUserId}
              onChange={(e) => setSalesUserId(e.target.value)}
              placeholder={t("integrations.fieldSalesUserPlaceholder")}
            />
          </Field>
        </div>
        <Button variant="primary" onClick={open}>
          <CalendarIcon size={14} /> {t("integrations.connect")}
          <ExternalLinkIcon size={12} />
        </Button>
      </Card>
    </PageContainer>
  );
}
